import * as React from "react";
import {
  AppBar,
  Box,
  Card,
  CardContent,
  Container,
  CssBaseline,
  IconButton,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
  TextField,
  Button,
  useMediaQuery,
} from "@mui/material";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useNavigate, useLocation } from "react-router-dom";
import { useEventData } from "../contexts/EventDataContext";
import { getAccessibleTextColor } from "../utils/colors";
import { getIcons } from "../utils/icon";
// NEW: Firestore imports
import { addDoc, collection } from "firebase/firestore";
import { db } from "../firebase/config";

// Removed static theme; dynamic theme will be created inside component

function PreviewCard({ src, name, message, isMobile, fontColor, primaryColor, iconLike, showLikes = true }) {
  const [liked, setLiked] = React.useState(false);
  const [likes, setLikes] = React.useState(0);

  const toggleLike = () => {
    setLiked((prev) => {
      if (prev) {
        setLikes((c) => Math.max(0, c - 1));
        return false;
      } else {
        setLikes((c) => c + 1);
        return true;
      }
    });
  };


  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        bgcolor: "background.paper",
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
        width: "100%",
        maxWidth: { xs: 300, sm: 350, md: 350 },
        minHeight: { xs: 350, md: 500 },
        display: "flex",
        flexDirection: "column",
        p: 1.5,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 6,
          right: 6,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          zIndex: 2,
        }}
      >
        {showLikes && (
        <IconButton
          size="small"
          aria-label="like"
          onClick={toggleLike}
          sx={{
            bgcolor: "white",
            boxShadow: 1,
            "&:hover": { bgcolor: "white" },
            border: liked ? "1px solid #e91e63" : "1px solid #ddd",
          }}
        >
          {getIcons(iconLike, { color: liked ? "#e91e63" : primaryColor, fontSize: 20 })}
        </IconButton>
        )}

      </Box>

      <Box
        sx={{
          width: "100%",
          flex: "0 0 75%",
          minHeight: isMobile ? 250 : 400,
          backgroundImage: `url(${src})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          borderRadius: 1,
          position: "relative",
        }}
      />
      <CardContent
        sx={{
          textAlign: "center",
          pt: 2,
          flex: "1",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: fontColor,
            lineHeight: 1.4,
            letterSpacing: 0.4,
            mb: 1,
            minHeight: 40,
            whiteSpace: "pre-line",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {message || "No message"}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: fontColor }}
        >
          {name || "No name"}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function WishPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    eventData,
    gotoHome,
    gotoGift,
    currentEventId,
    uploadImageToStorage,
  } = useEventData();

  // เช็คว่าเป็นโหมด preview หรือไม่
  const isPreview = location.pathname.includes("/preview/");

  const themeData = eventData.theme || {};
  const wishConfig = eventData.wish || {};
  const backgroundColor = themeData.backgroundColor?.value || "#FFF7DC";
  const globalBackgroundImage = themeData.backgroundImage?.value;
  const primaryColor = themeData.mainColor?.value || "#8B4513";
  const secondaryColor = themeData.secondaryColor?.value || "#FFD700";
  const fontColor = themeData.fontColor?.value || "#7A3E00";
  const confirmButtonEnabled = wishConfig.confirmButton?.enabled?.value ?? true;
  const confirmButtonText = wishConfig.confirmButton?.text?.value || "Confirm";
  const confirmButtonIcon =
    wishConfig.confirmButton?.icon?.value || "check_circle";
  const nameFieldEnabled = wishConfig.senderNameField?.enabled?.value ?? true;
  const namePlaceholder =
    wishConfig.senderNameField?.placeholder?.value || "Enter your name";
  const nameCharacterLimit =
    wishConfig.senderNameField?.characterLimit?.value || 50;
  const wishFieldEnabled = wishConfig.wishField?.enabled?.value ?? true;
  const wishPlaceholder =
    wishConfig.wishField?.placeholder?.value || "Write your wish message...";
  const wishCharacterLimit = wishConfig.wishField?.characterLimit?.value || 200;
  const previewButtonEnabled = wishConfig.previewButton?.enabled?.value ?? true;
  const previewButtonIcon = wishConfig.previewButton?.icon?.value || "preview";

  const dynamicTheme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: primaryColor,
            contrastText: getAccessibleTextColor(primaryColor),
          },
          secondary: {
            main: secondaryColor,
            contrastText: getAccessibleTextColor(secondaryColor),
          },
          background: { default: backgroundColor, paper: backgroundColor },
        },
        shape: { borderRadius: 16 },
        typography: {
          fontFamily: [
            "Inter",
            "-apple-system",
            "BlinkMacSystemFont",
            "Segoe UI",
            "Roboto",
            "Helvetica",
            "Arial",
            "sans-serif",
          ].join(","),
        },
      }),
    [primaryColor, secondaryColor, backgroundColor, fontColor]
  );

  const isMobile = useMediaQuery(dynamicTheme.breakpoints.down("md"));

  const [name, setName] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [image, setImage] = React.useState(
    // "https://picsum.photos/id/237/400/400"
    "https://placehold.co/400x400/FFFFFF/8A5918?text=Preview"

  );
  const [backgroundImage, setBackgroundImage] = React.useState(null); // preview data URL
  // NEW: store raw file & submission states
  const [wishFile, setWishFile] = React.useState(null);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [submitSuccess, setSubmitSuccess] = React.useState(false);

  const handleFileChange = (e) => {
    // เอา isPreview guard ออก ให้ upload ได้ใน preview
    const file = e.target.files[0];
    if (file) {
      setWishFile(file);
      setUploadProgress(0);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
        setBackgroundImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    // เอา isPreview guard ออก ให้ลบรูปได้ใน preview
    setBackgroundImage(null);
    setImage("https://placehold.co/400x400/FFFFFF/8A5918?text=Preview");
    setWishFile(null);
    setUploadProgress(0);
  };

  const handleConfirm = async () => {
    if (isPreview) return; // บล็อคเซฟในโหมด preview เท่านั้น
    if (!currentEventId) return;
    if (
      (nameFieldEnabled && !name.trim()) ||
      (wishFieldEnabled && !message.trim())
    )
      return;
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    try {
      let imageUrl = null;
      if (wishFile) {
        const res = await uploadImageToStorage(wishFile, {
          folder: "wishImages",
          eventId: currentEventId,
          onProgress: (p) => setUploadProgress(p),
        });
        if (res.success) imageUrl = res.url;
        else throw new Error(res.message || "Upload failed");
      }
      await addDoc(collection(db, "events", currentEventId, "wishes"), {
        eventId: currentEventId,
        name: nameFieldEnabled ? name.trim() || null : null,
        message: wishFieldEnabled ? message.trim() || null : null,
        imageUrl,
        createdAt: new Date(),
        likes: 0,
      });
      setSubmitSuccess(true);
      setName("");
      setMessage("");
      removeImage();
      navigate(`/${currentEventId}/wish-landing`, { replace: true });
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={dynamicTheme}>
      <CssBaseline />
      <AppBar
        position="sticky"
        color="primary"
        elevation={0}
        sx={{ bgcolor: primaryColor }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={gotoHome}
          >
            <ArrowBackIosNewRoundedIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              letterSpacing: 4,
              mr: 6,
              color: getAccessibleTextColor(primaryColor),
            }}
          >
            Wish
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: "calc(100vh - 64px)",
          backgroundImage: globalBackgroundImage ? `url(${globalBackgroundImage})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <Box
          sx={{
            flex: { xs: "none", md: 1 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 2, md: 2 },
          }}
        >
          {!isMobile && (
            <PreviewCard
              src={image}
              name={name}
              message={message}
              isMobile={isMobile}
              fontColor={fontColor}
              primaryColor={primaryColor}
              showLikes={previewButtonEnabled} // Hide likes in preview
              iconLike={previewButtonIcon}// Use a default icon for preview
            />
          )}
        </Box>

        <Box
          sx={{
            flex: { xs: "none", md: 1 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            p: { xs: 2, md: 4 },
          }}
        >
          <Box sx={{ maxWidth: "100%", mx: "auto", width: "100%", bgcolor: globalBackgroundImage ? "rgba(255,255,255,0.8)" : "transparent", backdropFilter: globalBackgroundImage ? "blur(10px)" : "none", p: 4, borderRadius: 2 }}>
            <Box
              sx={{
                position: "relative",
                p: 2,
                textAlign: "center",
                height: 300,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: 2,
                mb: 3,
                border: `2px dashed ${primaryColor}55`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: secondaryColor,
              }}
            >
              {backgroundImage ? (
                <>
                  <img
                    src={backgroundImage}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "275px",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: 12,
                    }}
                  />
                    <IconButton
                      onClick={removeImage}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "white",
                        border: `2px solid ${primaryColor}`,
                        "&:hover": { backgroundColor: "#eee" },
                      }}
                    >
                      <CloseIcon sx={{ color: primaryColor }} />
                    </IconButton>
                  {isSubmitting && wishFile && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        left: 0,
                        right: 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: primaryColor, fontWeight: 600 }}
                      >
                        Uploading {Math.round(uploadProgress)}%
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.5,
                          height: 6,
                          bgcolor: "#fff",
                          mx: 2,
                          borderRadius: 3,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${uploadProgress}%`,
                            height: "100%",
                            bgcolor: primaryColor,
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  sx={{ height: 300, justifyContent: "center" }}
                >
                  <AttachFileIcon
                    sx={{ color: primaryColor, fontSize: 28, mb: 3 }}
                  />
                  <Button
                    variant="contained"
                    component="label"
                    sx={{
                      backgroundColor: primaryColor,
                      color: getAccessibleTextColor(primaryColor),
                      borderRadius: 2,
                      px: 4,
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: primaryColor,
                        opacity: 0.9,
                      },
                      opacity: isPreview ? 0.7 : 1,
                    }}
                  >
                    Upload your pic!
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                  </Button>
                </Box>
              )}
            </Box>

            {nameFieldEnabled && (
              <TextField
                label="Name"
                fullWidth
                value={name}
                onChange={(e) => {
                  const v = e.target.value.slice(0, nameCharacterLimit);
                  setName(v);
                }}
                placeholder={namePlaceholder}
                helperText={`${name.length}/${nameCharacterLimit}`}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "right",
                    m: 0,
                    mt: 0.5,
                    color:
                      name.length >= nameCharacterLimit
                        ? "error.main"
                        : "text.secondary",
                    fontSize: 12,
                  },
                }}
                inputProps={{ maxLength: nameCharacterLimit }}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: secondaryColor,
                    borderColor: primaryColor,
                  },
                }}
                InputProps={{
                  endAdornment: name && (
                    <IconButton
                      onClick={() => setName("")}
                      size="small"
                      sx={{ mr: 0.5 }}
                    >
                      <HighlightOffIcon sx={{ color: primaryColor }} />
                    </IconButton>
                  ),
                }}
              />
            )}

            {wishFieldEnabled && (
              <TextField
                label="Message"
                fullWidth
                multiline
                minRows={4}
                value={message}
                onChange={(e) => {
                  const v = e.target.value.slice(0, wishCharacterLimit);
                  setMessage(v);
                }}
                placeholder={wishPlaceholder}
                helperText={`${message.length}/${wishCharacterLimit}`}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "right",
                    m: 0,
                    mt: 0.5,
                    color:
                      message.length >= wishCharacterLimit
                        ? "error.main"
                        : "text.secondary",
                    fontSize: 12,
                  },
                }}
                inputProps={{ maxLength: wishCharacterLimit }}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: secondaryColor,
                    borderColor: primaryColor,
                  },
                }}
                InputProps={{
                  endAdornment: message && (
                    <IconButton
                      onClick={() => setMessage("")}
                      size="small"
                      sx={{ alignSelf: "flex-start", mt: 0.5, mr: 0.5 }}
                    >
                      <HighlightOffIcon sx={{ color: primaryColor }} />
                    </IconButton>
                  ),
                }}
              />
            )}

            {submitError && (
              <Typography variant="body2" sx={{ color: "red", mb: 1 }}>
                {submitError}
              </Typography>
            )}
            {submitSuccess && (
              <Typography variant="body2" sx={{ color: "green", mb: 1 }}>
                Wish submitted successfully!
              </Typography>
            )}

            {confirmButtonEnabled && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={getIcons(confirmButtonIcon)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: 16,
                  backgroundColor: primaryColor,
                  color: getAccessibleTextColor(primaryColor),
                  "&:hover": { backgroundColor: primaryColor, opacity: 0.9 },
                  opacity: isSubmitting || isPreview ? 0.7 : 1,
                }}
                disabled={
                  isPreview || // บล็อคเฉพาะปุ่มเซฟใน preview
                  isSubmitting ||
                  (nameFieldEnabled && !name.trim()) ||
                  (wishFieldEnabled && !message.trim()) ||
                  name.length > nameCharacterLimit ||
                  message.length > wishCharacterLimit
                }
                onClick={handleConfirm}
              >
                {isPreview
                  ? "Preview mode"
                  : isSubmitting
                  ? "Submitting..."
                  : confirmButtonText || "Confirm"}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
